import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { checkAdminAuth, adminLogout } from "../lib/adminAuth";
import AdminIdleTimeout from "./AdminIdleTimeout";

const AdminShell = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const checkAuth = async () => {
      const adminData = await checkAdminAuth();
      if (adminData) {
        setAdmin(adminData);
      } else {
        navigate("/admin/login");
      }
      setLoading(false);
    };

    checkAuth();
  }, [navigate]);

  const handleLogout = async () => {
    const success = await adminLogout();
    if (success) {
      setAdmin(null);
      navigate("/admin/login");
    }
  };

  const handleIdleTimeout = () => {
    console.log("Admin session timed out due to inactivity");
    setAdmin(null);
    navigate("/admin/login?reason=timeout");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (!admin) {
    return null; // Will redirect to login
  }

  const navigation = [
    { name: "Dashboard", href: "/admin", icon: "📊" },
    { name: "Leads", href: "/admin/leads", icon: "🏠" },
    { name: "Contractors", href: "/admin/contractors", icon: "👥" },
    { name: "Alerts", href: "/admin/alerts", icon: "🚨" },
    { name: "Settings", href: "/admin/settings", icon: "⚙️" },
  ];

  return (
    <AdminIdleTimeout onTimeout={handleIdleTimeout}>
      <div className="min-h-screen bg-gray-50">
        {/* Navigation Sidebar */}
        <div className="fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg">
          <div className="flex flex-col h-full">
            {/* Logo/Header */}
            <div className="flex items-center h-16 px-6 bg-orange-500">
              <Link to="/" className="text-white text-xl font-bold">
                BuildBright Admin
              </Link>
            </div>

            {/* Navigation */}
            <nav className="flex-1 px-4 py-6 space-y-2">
              {navigation.map((item) => {
                const isActive =
                  location.pathname === item.href ||
                  (item.href === "/admin" && location.pathname === "/admin/");

                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                      isActive
                        ? "bg-orange-100 text-orange-700"
                        : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                    }`}
                  >
                    <span className="mr-3 text-lg">{item.icon}</span>
                    {item.name}
                  </Link>
                );
              })}
            </nav>

            {/* Admin Profile */}
            <div className="px-4 py-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-medium">
                      {admin.email ? admin.email.charAt(0).toUpperCase() : "A"}
                    </span>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900 truncate max-w-32">
                      {admin.email}
                    </p>
                    <p className="text-xs text-gray-500">Admin</p>
                  </div>
                </div>
                <button
                  onClick={handleLogout}
                  className="text-gray-400 hover:text-gray-600 text-sm"
                  title="Sign out"
                >
                  🚪
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="pl-64">
          {/* Top Bar */}
          <div className="bg-white shadow-sm border-b border-gray-200">
            <div className="px-6 py-4">
              <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold text-gray-900">
                  {navigation.find(
                    (item) =>
                      location.pathname === item.href ||
                      (item.href === "/admin" &&
                        location.pathname === "/admin/"),
                  )?.name || "Admin"}
                </h1>

                <div className="flex items-center space-x-4">
                  <Link
                    to="/"
                    className="text-sm text-gray-500 hover:text-gray-700"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View Site ↗
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Sign Out
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Page Content */}
          <main className="p-6">{children}</main>
        </div>
      </div>
    </AdminIdleTimeout>
  );
};

export default AdminShell;
